import api from "@/stores/api.js";
import ErrorUtils from "@/utils/error.utils.js";

const WEBSOCKET_PROTOCOL = import.meta.env.VITE_WEBSOCKET_PROTOCOL;
const ENDPOINT = 'question';
const API_URL = import.meta.env.VITE_API_URL;

class QuestionService {

    generateTags(tagsMessage, item, isGeneratingTags, selectedModel) {

        this.closeTagsSocket();
        
        const data = {
            technologicalArea: item.value.information.technologicalArea?.name,
            course: item.value.information.course?.name,
            subfunction: item.value.information.subfunction?.name,
            capacity: item.value.information.capacity?.name,
            knowledge: item.value.information.knowledge.map(k => k.name),
            tags: item.value.information.tags,
            modelName: selectedModel ? selectedModel.name : null
        }

        this.tagsSocket = new WebSocket(`${WEBSOCKET_PROTOCOL}://${API_URL}/ws/${ENDPOINT}/tags`);

        isGeneratingTags.value = true;

        this.tagsSocket.onmessage = (event) => {
            try {
                const response = JSON.parse(event.data);
                if ('json' !== response.type) {
                    tagsMessage.value = {
                        error: response.error,
                        message: response.data
                    };
                    if (response.error) {
                        isGeneratingTags.value = false;
                    }
                    return;
                }

                const generatedTags = JSON.parse(response.data);
                item.value.information.tags = generatedTags;
                tagsMessage.value = {
                    error: false,
                    message: `${generatedTags.length} tags geradas com sucesso!`
                };
                isGeneratingTags.value = false;

                setTimeout(() => {
                    if (tagsMessage.value && !tagsMessage.value.error) {
                        tagsMessage.value = {};
                    }
                }, 3000);

                this.tagsSocket.close();
            } catch (e) {
                tagsMessage.value = {
                    error: true,
                    message: 'Erro ao processar as tags geradas. Tente novamente.'
                };
                isGeneratingTags.value = false;
                ErrorUtils.handleError(e);
            }
        }

        this.tagsSocket.onerror = (error) => {
            console.error('[TAGS] Erro no WebSocket:', error);
            tagsMessage.value = {
                error: true,
                message: 'Erro de conexão. Tente novamente.'
            };
            isGeneratingTags.value = false;
        }

        this.tagsSocket.onclose = (event) => {
            if (event.code !== 1000 && isGeneratingTags.value) {
                tagsMessage.value = {
                    error: true,
                    message: 'Conexão perdida. Tente novamente.'
                };
                isGeneratingTags.value = false;
            }
        }

        this.tagsSocket.onopen = () => {
            const message = {
                "data": data
            }
            this.tagsSocket.send(JSON.stringify(message));
        }
    }

    generateQuestion(generateErrorMessage, item, isGenerating, selectedModel, emits) {
        this.socket = new WebSocket(`${WEBSOCKET_PROTOCOL}://${API_URL}/ws/${ENDPOINT}/generate`);

        isGenerating.value = true;
        item = {
            ...item,
            modelName: selectedModel ? selectedModel.name : null
        }

        this.socket.onmessage = (event) => {
            try {
                const response = JSON.parse(event.data);
                if ('json' !== response.type) {
                    generateErrorMessage.value = {
                        error: response.error,
                        message: response.data
                    };
                    if (response.error) {
                        isGenerating.value = false;
                    }
                    return;
                }

                let question = response.data;
                question = {
                    ...question,
                    correctAnswer: String(question.correctAnswer),
                    alternatives: question.alternatives.map(a => {
                        return {
                            ...a,
                            number: String(a.number)
                        }
                    })
                }

                const questions = this.getQuestions();

                let newItem;
                if (!item.id) {
                    const id = questions.reduce((acc, q) => q.id > acc ? q.id : acc, 0);
                    newItem = {
                        ...item,
                        id: id + 1,
                        question: question
                    }
                } else {
                    newItem = {
                        ...item,
                        question: question
                    }
                }
                questions.push(newItem);

                this.setQuestions(questions);
                emits('updateItem', newItem);
                emits('updateItems');
                emits('visualizeItem', newItem);

                generateErrorMessage.value = '';
                isGenerating.value = false;

                this.socket.close();
            } catch (e) {
                generateErrorMessage.value = event.data;
                isGenerating.value = false;
                ErrorUtils.handleError(e);
            }
        }

        this.socket.onopen = () => {
            const message = {
                "data": item
            }
            this.socket.send(JSON.stringify(message));
        }
    }

    updateQuestion(item) {
        const questions = this.getQuestions();
        const index = questions.findIndex(q => q.id === item.id && q.question.lastEditedAt === item.question.lastEditedAt);
        questions[index] = item;
        this.setQuestions(questions);
    }

    getQuestions() {
        return JSON.parse(localStorage.getItem('questions')) || [];
    }

    setQuestions(questions) {
        localStorage.setItem('questions', JSON.stringify(questions));
    }

    removeQuestion(id) {
        const questions = this.getQuestions();
        const updatedQuestions = questions.filter(question => question.id !== id);
        this.setQuestions(updatedQuestions);
    }

    getTechnologicalAreas() {
        return this.doGet('technological-area');
    }

    getCoursesByTechnologicalArea(idTechnologicalArea) {
        return this.doGet(`technological-area/${idTechnologicalArea}/course`);
    }

    getCapacities(idCourse) {
        return this.doGet(`capacity?idCourse=${idCourse}`);
    }

    getSubfunctionsByCapacity(idCapacity) {
        return this.doGet(`subfunction?idCapacity=${idCapacity}`);
    }

    getKnowledgeBySubfunctionCapacity(idSubfunction, idCapacity) {
        return this.doGet(`subfunction/${idSubfunction}/capacity/${idCapacity}/knowledge`);
    }

    saveFeedback(idQuestion, version, feedback) {
        return this.doPost(`${idQuestion}/${version}/feedback`, feedback);
    }

    closeTagsSocket() {
        if (this.tagsSocket && this.tagsSocket.readyState === WebSocket.OPEN) {
            this.tagsSocket.close(1000, 'Closing tags socket');
            this.tagsSocket = null;
        }
    }

    doGet = (path, body) => {
        try {
            return api().get(`/${ENDPOINT}/${path}`, {data: body});
        } catch (e) {
            ErrorUtils.handleError(e);
        }
    }

    doPost = (path, body) => {
        try {
            return api().post(`/${ENDPOINT}/${path}`, body);
        } catch (e) {
            ErrorUtils.handleError(e);
        }
    }

    deleteQuestion(idQuestion) {
        try {
            return api().delete(`/${ENDPOINT}/${idQuestion}`);
        } catch (e) {
            ErrorUtils.handleError(e);
        }
    }

    getFeedbackUrl() {
        try {
            return api().get(`/${ENDPOINT}/feedback/url`)
                .then(response => response.data);
        } catch (e) {
            ErrorUtils.handleError(e);
        }
    }

    // Funcionalidade de gerar descrição de imagem desativada
    // generateImageDescription(base64Image) {
    //     try {
    //         const payload = {
    //             image_base64: base64Image,
    //             model_name: "GPT"
    //         };
    //         return api().post(`/${ENDPOINT}/generate-image-description`, payload);
    //     } catch (e) {
    //         ErrorUtils.handleError(e);
    //         throw e;
    //     }
    // }
}

export default new QuestionService();